<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import AccountLayout from '@/components/layout/AccountLayout.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()

/** Section courante (clé de nav), fournie par la meta de route. */
const section = computed(() => (route.meta.accountSection as string) ?? 'dashboard')
const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))

const title = computed(() => t(`auth.account.nav.${section.value}`))
useHead({ title: computed(() => `${title.value} — MySmerra`) })

/** Accès réservé : sans session, retour à la page de connexion. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})
</script>

<template>
  <AccountLayout :active="section">
    <section class="ph">
      <h1 class="ph__title">{{ title }}</h1>
      <p class="ph__note">{{ t('auth.account.placeholder') }}</p>
    </section>
  </AccountLayout>
</template>

<style scoped>
.ph {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: clamp(1.5rem, 4vw, 2.5rem);
}
.ph__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.ph__note { font-size: var(--text-sm); color: var(--text-muted); }
</style>
